// The roles a user can hold at one location. Shared by both directions of the
// same assignment: the Users page (locations as rows, one user at a time) and
// the locations settings page (users as rows, one location at a time).
//
// A role is a named collection of permissions and nothing else — what each one
// grants lives in the backend's RoleMatrix and is never restated here, so the
// two cannot drift. This app gates on permissions (see constants/permissions.ts)
// and uses roles only to assign them.
//
// Account administration is deliberately NOT among them: it is a flag on the
// user, because it is not something anyone holds at a centre. See
// `accountAdmin` on UserDetailResponse.

// Imported as a TYPE ONLY, and the literals below are held to it by
// `satisfies`: depending on the generated enum's runtime value would make every
// roles grid break in any test that mocks the generated module. The label maps
// are keyed by the union, so a role added on the backend is a compile error here
// rather than a column that quietly goes missing.
import type { RoleAssignmentRequestRolesEnum } from '../api/generated';

export type LocationRole = RoleAssignmentRequestRolesEnum;

export const LOCATION_ROLES = {
  Practitioner: 'PRACTITIONER',
  Planner: 'PLANNER',
  Manager: 'MANAGER',
  CentralAdministration: 'CENTRAL_ADMINISTRATION'
} as const satisfies Record<string, LocationRole>;

// Listed narrowest-first, matching the backend enum order, so the rights grids
// read as a widening scale from left to right.
export const LOCATION_ROLE_ORDER: readonly LocationRole[] = [
  LOCATION_ROLES.Practitioner,
  LOCATION_ROLES.Planner,
  LOCATION_ROLES.Manager,
  LOCATION_ROLES.CentralAdministration
];

export const LOCATION_ROLE_LABEL_KEYS: Record<LocationRole, string> = {
  [LOCATION_ROLES.Practitioner]: 'config-role-practitioner',
  [LOCATION_ROLES.Planner]: 'config-role-planner',
  [LOCATION_ROLES.Manager]: 'config-role-manager',
  [LOCATION_ROLES.CentralAdministration]: 'config-role-central-administration'
};

// Short forms for the rights-grid column headers, which are one narrow column
// per role and cannot fit "Centraladministration". The full label and the
// description below are shown as the column's tooltip.
export const LOCATION_ROLE_SHORT_LABEL_KEYS: Record<LocationRole, string> = {
  [LOCATION_ROLES.Practitioner]: 'config-role-practitioner-short',
  [LOCATION_ROLES.Planner]: 'config-role-planner-short',
  [LOCATION_ROLES.Manager]: 'config-role-manager-short',
  [LOCATION_ROLES.CentralAdministration]: 'config-role-central-administration-short'
};

// What the role is for, in one line. Shown as the column tooltip so an operator
// picking a role does not have to know the permission catalogue.
export const LOCATION_ROLE_DESCRIPTION_KEYS: Record<LocationRole, string> = {
  [LOCATION_ROLES.Practitioner]: 'config-role-practitioner-hint',
  [LOCATION_ROLES.Planner]: 'config-role-planner-hint',
  [LOCATION_ROLES.Manager]: 'config-role-manager-hint',
  [LOCATION_ROLES.CentralAdministration]: 'config-role-central-administration-hint'
};

// The role set a user holds at one location, in the display order above and with
// no duplicates. The single source for the assignment shape: each page extends
// it with the id of the axis it iterates (userId on the locations page,
// locationId on the Users page).
export const sortRoles = (roles: readonly LocationRole[]): LocationRole[] =>
  LOCATION_ROLE_ORDER.filter((role) => roles.includes(role));
